import styled from '@emotion/styled'
import * as RadixTooltip from '@radix-ui/react-tooltip'
import { ReactNode } from 'react'

import { Div } from './Div'
import { Text } from './Text'
import { Color, theme } from '../constants'
import { Intent, getIntentProps } from '../constants/intent'

const Content = styled(RadixTooltip.Content)<{
  background: Color
  color: Color
  linkColor: Color
}>(({ background, color, linkColor }) => ({
  background,
  borderRadius: theme.border.radius.small,
  boxShadow: theme.boxShadow.normal,
  color,
  fontSize: 14,
  maxWidth: 160,
  a: {
    color: linkColor,
    fontWeight: theme.font.weight.bold,
    textDecoration: 'underline',
  },
  zIndex: theme.zIndex.tooltip,
}))

const TooltipContent = ({
  children,
  text,
  side,
  intent,
}: {
  children?: ReactNode
  text?: string
  side?: RadixTooltip.TooltipContentProps['side']
  intent: Intent
}) => {
  const { backgroundColor, color, linkColor } = getIntentProps(intent)

  return (
    <Content
      side={side}
      background={backgroundColor}
      color={color}
      linkColor={linkColor}
    >
      <RadixTooltip.Arrow fill={backgroundColor} />
      <Div styles={{ textAlign: 'center', p: 1 }}>
        {text && <Text>{text}</Text>}
        {children}
      </Div>
    </Content>
  )
}

type TooltipProps = {
  text?: string
  side?: RadixTooltip.TooltipContentProps['side']
  intent?: Intent
  open?: boolean
  children: ReactNode
}

const Tooltip = ({
  children,
  text,
  side,
  intent = 'info',
  open,
}: TooltipProps) => {
  return (
    <RadixTooltip.Root open={open}>
      {text && (
        <RadixTooltip.Portal>
          <TooltipContent text={text} side={side} intent={intent} />
        </RadixTooltip.Portal>
      )}
      {children}
    </RadixTooltip.Root>
  )
}

Tooltip.Content = TooltipContent
Tooltip.Trigger = RadixTooltip.Trigger

export { Tooltip }

export type { TooltipProps }

export const TooltipProvider = ({ children }: { children: ReactNode }) => (
  <RadixTooltip.Provider>{children}</RadixTooltip.Provider>
)
